import React, { useRef, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { makeGetTask } from 'selectors/tasks';
import { makeGetMilestone } from 'selectors/milestones';

import { updateTask } from 'actions/tasks';
import { showModal } from 'actions/modal';

import { canChangeStartDate } from 'utils/task';
import { useProjectHasAutoMilestones } from 'shared-hooks/project';

import DateElement from 'components/date-element';

const TaskStartDatePicker = ({ id, xAlign, defaultXPosition, showQuickOptions, inline, autoShow, withTitle, taskDueDate, taskStartDate, milestone, projectId, onChange, onShow, onHide, onComplete, onCancel, provideController, children }) => {
    const dateCtrl = useRef(null);
    const { t } = useTranslation('task');
    const hasAutoMilestones = useProjectHasAutoMilestones(projectId);

    const doUpdate = useCallback(({ nextDate, nextDueDate, milestone }) => {
        if (!hasAutoMilestones && nextDate && !canChangeStartDate(nextDate, milestone, false)) {
            showModal('EXTEND_MILESTONES_MODAL', {
                milestones: [milestone],
                nextDate,
                isStartDate: true,
                onSuccess: () => {
                    updateTask(id, {
                        due_date: nextDueDate,
                        start_date: nextDate
                    });
                }
            });

            return;
        };

        updateTask(id, {
            due_date: nextDueDate,
            start_date: nextDate
        });
    }, [id, hasAutoMilestones]);

    const change = useCallback((nextDate, updateDueDate) => {
        let nextDueDateFormatted;

        if (updateDueDate) {
            const diff = Math.max(0, moment(taskDueDate).diff(taskStartDate, 'days')) || 2;

            nextDueDateFormatted = moment(nextDate).add(diff, 'days').toISOString();
        }

        const nextDateFormatted = nextDate && moment(nextDate).toISOString();

        if (taskStartDate === nextDateFormatted) {
            return;
        }

        const nextProps = {
            nextDate: nextDateFormatted,
            nextDueDate: nextDueDateFormatted,
            milestone,
            projectId
        };

        if (onChange) {
            onChange(nextProps);
            return;
        }

        doUpdate(nextProps);
    }, [taskDueDate, taskStartDate, milestone, projectId, onChange, doUpdate]);

    const handleChange = useCallback((nextDate, updateDueDate) => {
        change(nextDate, updateDueDate);

        onComplete && onComplete();

        dateCtrl.current?.hide();
    }, [change, dateCtrl, onComplete]);

    const handleRemove = useCallback(() => {
        const nextProps = {
            nextDate: null
        };

        if (onChange) {
            onChange(nextProps);
            return;
        }

        doUpdate(nextProps);

        onComplete && onComplete();

        dateCtrl.current?.hide();
    }, [onChange, dateCtrl, doUpdate]);

    useEffect(() => {
        const ctrl = {
            show: colons => {
                dateCtrl.current?.show();
            },
            hide: () => {
                dateCtrl.current?.hide();
            },
            save: () => {
                change(dateCtrl.current?.getSelected());
            }
        };

        if (provideController) {
            if ('current' in provideController) {
                provideController.current = ctrl;
            } else {
                provideController && provideController(ctrl);
            }
        }

        return () => {
            if (provideController) {
                if ('current' in provideController) {
                    provideController.current = null;
                } else {
                    provideController && provideController(null);
                }
            }
        };
    }, []);

    return (
        <DateElement
            xAlign={xAlign}
            defaultXPosition={defaultXPosition}
            showQuickOptions={showQuickOptions}
            inline={inline}
            autoShow={autoShow}

            selected={taskStartDate}
            title={withTitle && (taskStartDate ? t('actions.startDate.titleChange') : t('actions.startDate.title'))}
            removePopoutTitle={ t('actions.startDate.remove.title') }
            removeBodyText={ t('actions.startDate.remove.description') }
            relatedBefore={taskDueDate}
            relatedCopy={ t('actions.startDate.related.title') }
            relatedConfirmText={ t('actions.startDate.related.confirm') }
            showTimeSelect={true}

            onChange={handleChange}
            onRemove={handleRemove}
            onShow={onShow}
            onHide={onHide}
            onCancel={onCancel}
            provideController={dateCtrl}
        >
            { children || null }
        </DateElement>
    )

}

const makeMapStateToProps = () => {
    const getTask = makeGetTask();
    const getMilestone = makeGetMilestone();

    return ({ tasks, milestones }, ownProps) => {
        const task = getTask(tasks.items, ownProps.id);

        return {
            taskDueDate: task?.due_date,
            taskStartDate: task?.start_date,
            milestone: task?.milestone && getMilestone(milestones.items, task.milestone),
            projectId: task?.project
        }
    }
}

export default connect(makeMapStateToProps)(React.memo(TaskStartDatePicker));
